import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useNotification } from '@/core/contexts/NotificationContext';
import { useAuth } from '@/core/contexts/AuthContext';
import { QUERY_KEYS } from '@/utils/constants/queryKeys';

/**
 * Hook for handling real-time notifications via WebSocket
 * Automatically invalidates relevant queries when real-time events are received
 */
export const useRealTimeNotifications = () => {
  const queryClient = useQueryClient();
  const { notifyInfo } = useNotification();
  const { user } = useAuth();
  const processedUpdates = useRef(new Set()); // Track processed updates to prevent duplicates

  // Reduced logging to prevent console spam
  // console.log('🔌 [useRealTimeNotifications] Hook initialized');
  // console.log('🔌 [useRealTimeNotifications] User:', user);

  useEffect(() => {
    if (!user) {
      // console.log('🔌 [useRealTimeNotifications] No user, skipping setup');
      return;
    }

    console.log('🔌 [useRealTimeNotifications] Setting up event listeners for user:', user.user_id || user.id);

    const handleNewMessage = (event) => {
      const messageData = event.detail;
      console.log('📨 Real-time new message:', messageData);
      
      // Invalidate message-related queries - use partial matching to catch all thread queries
      queryClient.invalidateQueries({ 
        predicate: (query) => query.queryKey[0] === QUERY_KEYS.THREADS 
      });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.MESSAGES, messageData.threadId] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.EVENT_NOTIFICATIONS] });
      
      // Show notification if it's not from current user
      if (messageData.sender_id !== user.userId && messageData.sender_id !== user.user_id) {
        notifyInfo(`💬 New message from ${messageData.sender_name || 'someone'}`);
        
        // Show browser notification if permission granted
        if ('Notification' in window && Notification.permission === 'granted') {
          try {
            new Notification('HFRConnect - New Message', {
              body: `${messageData.sender_name}: ${messageData.message_preview || 'New message'}`,
              icon: '/logo152.png',
              tag: 'hfrconnect-message'
            });
          } catch (error) {
            console.error('Error showing browser notification:', error);
          }
        }
      }
    };

    const handleUserStatus = (event) => {
      const statusData = event.detail;
      console.log('👤 Real-time user status:', statusData);
      
      // Update user status in cache
      queryClient.setQueryData([QUERY_KEYS.USER_STATUS, statusData.user_id], statusData);
      
      // Optionally show status change notifications for important users
      // (You could add logic here to only show for contacts or active conversations)
    };

    const handleTypingIndicator = (event) => {
      const typingData = event.detail;
      console.log('⌨️ Real-time typing indicator:', typingData);
      
      // Update typing indicators in cache
      const currentTyping = queryClient.getQueryData([QUERY_KEYS.TYPING, typingData.threadId]) || {};
      const newTyping = { ...currentTyping };
      
      if (typingData.isTyping) {
        newTyping[typingData.userId] = {
          userName: typingData.userName,
          timestamp: typingData.timestamp
        };
      } else {
        delete newTyping[typingData.userId];
      }
      
      queryClient.setQueryData([QUERY_KEYS.TYPING, typingData.threadId], newTyping);
    };

    const handleNotification = (event) => {
      const notificationData = event.detail;
      console.log('🔔 [useRealTimeNotifications] Real-time notification received:', notificationData.event_type || notificationData.type);
      
      // Skip showing popup notifications ONLY for system events (NOT regular notifications like ADD/REMOVE_FROM_WATCHLIST)
      if (notificationData.event_type === 'message_status_update' || 
          notificationData.event_type === 'analytics_update' ||
          notificationData.type === 'analytics_update') {
        console.log('🔔 [useRealTimeNotifications] Skipping popup notification for system event:', notificationData.event_type || notificationData.type);
        return;
      }
      
      // Invalidate notification queries to refresh the UI
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
      
      // Show notification using consistent primary theme color for design cohesion
      const notificationTitle = notificationData.data?.title || notificationData.title || 'New notification';
      
      if (notificationTitle && notificationTitle !== 'New notification') {
        console.log('🔔 [useRealTimeNotifications] Showing notification:', notificationTitle);
        // Use info severity for all notifications to get consistent primary theme color
        notifyInfo(notificationTitle);
      } else {
        console.log('🔔 [useRealTimeNotifications] Showing generic notification');
        notifyInfo('New notification received');
      }
      
      // Show browser notification; tag on notification_id deduplicates with push delivery
      if ('Notification' in window && Notification.permission === 'granted') {
        try {
          new Notification('HFRConnect', {
            body: notificationTitle,
            icon: '/logo152.png',
            tag: notificationData.notification_id || notificationData.data?.notification_id || 'hfrconnect-notification',
          });
        } catch (error) {
          console.error('Error showing browser notification:', error);
        }
      }
    };

    const handleAnalyticsUpdate = (event) => {
      const analyticsData = event.detail;
      console.log('📊 [useRealTimeNotifications] Raw analytics update received:', analyticsData);
      
      // Deduplicate by user + resource + timestamp - prevent processing same update multiple times
      // Include actor_user_id and resource_id to ensure only true duplicates are filtered
      const resourceId = analyticsData.resource_id || analyticsData.opportunity_id || 'no-resource';
      const actorUserId = analyticsData.actor_user_id || analyticsData.investor_id || 'no-user';
      const updateId = `${analyticsData.type}-${analyticsData.update_type}-${actorUserId}-${resourceId}-${analyticsData.timestamp}`;
      if (processedUpdates.current.has(updateId)) {
        console.log('📊 [useRealTimeNotifications] Skipping duplicate analytics update:', updateId);
        return;
      }
      processedUpdates.current.add(updateId);
      
      // Clean up old entries to prevent memory leaks (keep only last 100)
      if (processedUpdates.current.size > 100) {
        const entries = Array.from(processedUpdates.current);
        processedUpdates.current.clear();
        entries.slice(-50).forEach(id => processedUpdates.current.add(id));
      }
      
      console.log('📊 [useRealTimeNotifications] Processing analytics update:', analyticsData.type, analyticsData.update_type);
      
      // Handle different types of analytics updates
      switch (analyticsData.type) {
        case 'watchlist_metrics_update': {
          queryClient.invalidateQueries({ queryKey: ['metrics', 'opportunities'] });
          // Update the cache directly for immediate UI update - MUST MATCH useMetricsDashboard key exactly
          const dashboardQueryKey = [
            'metrics', 
            'dashboard', 
            user?.user_id, 
            user?.userType || user?.user_type, 
            user?.firstName, 
            user?.lastName
          ];
          
          const currentData = queryClient.getQueryData(dashboardQueryKey);
          
          if (currentData && currentData.metrics) {
            const updatedData = { ...currentData };
            const metrics = { ...updatedData.metrics };
            
            if (analyticsData.update_type === 'watchlist_added') {
              metrics.total_watchlists = (metrics.total_watchlists || 0) + 1;
              
              console.log('📊 [useRealTimeNotifications] Watchlist added - showing metric card animation');
              
              // Show metric card animation
              if (window.showFloatingNotification) {
                window.showFloatingNotification('add', 1, { 
                  targetWidget: 'watchlists-widget'
                });
              }
            } else if (analyticsData.update_type === 'watchlist_removed') {
              metrics.total_watchlists = Math.max(0, (metrics.total_watchlists || 0) - 1);
              
              console.log('📊 [useRealTimeNotifications] Watchlist removed - showing metric card animation');
              
              // Show metric card animation
              if (window.showFloatingNotification) {
                window.showFloatingNotification('remove', 1, { 
                  targetWidget: 'watchlists-widget'
                });
              }
            }
            
            updatedData.metrics = metrics;
            queryClient.setQueryData(dashboardQueryKey, updatedData);
            console.log('📊 [useRealTimeNotifications] ✅ Cache updated directly (no re-render)');
          } else {
            console.log('📊 [useRealTimeNotifications] ⚠️ No cache data, invalidating query (will cause re-render)');
            // Fallback: invalidate query if cache update fails
            queryClient.invalidateQueries({ queryKey: dashboardQueryKey });
          }
          break;
        }

        case 'inquiry_metrics_update': {
          // Update the cache directly for immediate UI update - MUST MATCH useMetricsDashboard key exactly
          const dashboardQueryKey = [
            'metrics', 
            'dashboard', 
            user?.user_id, 
            user?.userType || user?.user_type, 
            user?.firstName, 
            user?.lastName
          ];
          
          const currentData = queryClient.getQueryData(dashboardQueryKey);
          
          if (currentData && currentData.metrics) {
            const updatedData = { ...currentData };
            const metrics = { ...updatedData.metrics };
            
            if (analyticsData.update_type === 'inquiry_received') {
              metrics.unread_messages = (metrics.unread_messages || 0) + 1;
              
              console.log('📊 [useRealTimeNotifications] Inquiry received - showing metric card animation');
              
              // Show metric card animation
              if (window.showFloatingNotification) {
                window.showFloatingNotification('add', 1, { 
                  targetWidget: 'inquiries-widget'
                });
              }
            }
            
            updatedData.metrics = metrics;
            queryClient.setQueryData(dashboardQueryKey, updatedData);
            console.log('📊 [useRealTimeNotifications] ✅ Inquiry cache updated directly (no re-render)');
          } else {
            console.log('📊 [useRealTimeNotifications] ⚠️ No cache data for inquiry, invalidating query (will cause re-render)');
            // Fallback: invalidate query if cache update fails
            queryClient.invalidateQueries({ queryKey: dashboardQueryKey });
          }
          break;
        }

        case 'view_metrics_update': {
          queryClient.invalidateQueries({ queryKey: ['metrics', 'opportunities'] });
          // Update the cache directly for immediate UI update - MUST MATCH useMetricsDashboard key exactly
          const dashboardQueryKey = [
            'metrics', 
            'dashboard', 
            user?.user_id, 
            user?.userType || user?.user_type, 
            user?.firstName, 
            user?.lastName
          ];
          
          const currentData = queryClient.getQueryData(dashboardQueryKey);
          
          if (currentData && currentData.metrics) {
            const updatedData = { ...currentData };
            const metrics = { ...updatedData.metrics };
            
            if (analyticsData.update_type === 'opportunity_viewed') {
              metrics.total_views = (metrics.total_views || 0) + 1;
              
              console.log('📊 [useRealTimeNotifications] View received - showing metric card animation');
              
              // Show metric card animation
              if (window.showFloatingNotification) {
                window.showFloatingNotification('add', 1, { 
                  targetWidget: 'views-widget'
                });
              }
            }
            
            updatedData.metrics = metrics;
            queryClient.setQueryData(dashboardQueryKey, updatedData);
            console.log('📊 [useRealTimeNotifications] ✅ View cache updated directly (no re-render)');
          } else {
            console.log('📊 [useRealTimeNotifications] ⚠️ No cache data for view, invalidating query (will cause re-render)');
            // Fallback: invalidate query if cache update fails
            queryClient.invalidateQueries({ queryKey: dashboardQueryKey });
          }
          break;
        }

        case 'download_metrics_update': {
          // Update the cache directly for immediate UI update - MUST MATCH useMetricsDashboard key exactly
          const dashboardQueryKey = [
            'metrics', 
            'dashboard', 
            user?.user_id, 
            user?.userType || user?.user_type, 
            user?.firstName, 
            user?.lastName
          ];
          
          const currentData = queryClient.getQueryData(dashboardQueryKey);
          
          if (currentData && currentData.metrics) {
            const updatedData = { ...currentData };
            const metrics = { ...updatedData.metrics };
            
            if (analyticsData.update_type === 'document_downloaded') {
              metrics.document_downloads_count = (metrics.document_downloads_count || 0) + 1;
              
              console.log('📊 [useRealTimeNotifications] Download received - showing metric card animation');
              
              // Show metric card animation
              if (window.showFloatingNotification) {
                window.showFloatingNotification('add', 1, { 
                  targetWidget: 'document_downloads-widget'
                });
              }
            }
            
            updatedData.metrics = metrics;
            queryClient.setQueryData(dashboardQueryKey, updatedData);
          } else {
            // Fallback: invalidate query if cache update fails
            queryClient.invalidateQueries({ queryKey: dashboardQueryKey });
          }
          break;
        }

        case 'document_metrics_update': {
          // Handle document upload notifications for investors
          if (analyticsData.update_type === 'document_uploaded') {
            console.log('📊 [useRealTimeNotifications] Document uploaded - showing metric card animation');
            
            // Update the cache directly for immediate UI update - MUST MATCH useMetricsDashboard key exactly
            const dashboardQueryKey = [
              'metrics', 
              'dashboard', 
              user?.user_id, 
              user?.userType || user?.user_type, 
              user?.firstName, 
              user?.lastName
            ];
            
            const currentData = queryClient.getQueryData(dashboardQueryKey);
            
            if (currentData && currentData.metrics) {
              const updatedData = { ...currentData };
              const metrics = { ...updatedData.metrics };
              
              metrics.new_documents = (metrics.new_documents || 0) + 1;
              
              updatedData.metrics = metrics;
              queryClient.setQueryData(dashboardQueryKey, updatedData);
              console.log('📊 [useRealTimeNotifications] ✅ Document cache updated directly (no re-render)');
            } else {
              console.log('📊 [useRealTimeNotifications] ⚠️ No cache data for documents, invalidating query');
              queryClient.invalidateQueries({ queryKey: dashboardQueryKey });
            }
            
            // Show metric card animation - use correct widget ID
            if (window.showFloatingNotification) {
              window.showFloatingNotification('add', 1, { 
                targetWidget: 'documents_uploaded-widget'
              });
            }
            
            // Also invalidate notifications for the notification list
            queryClient.invalidateQueries({ queryKey: ['notifications'] });
          }
          break;
        }

        case 'opportunity_metrics_update': {
          // Handle new opportunity and closing soon notifications for investors
          if (analyticsData.update_type === 'new_opportunity') {
            console.log('📊 [useRealTimeNotifications] New opportunity - showing metric card animation');
            
            // Update the cache directly for immediate UI update
            const dashboardQueryKey = [
              'metrics', 
              'dashboard', 
              user?.user_id, 
              user?.userType || user?.user_type, 
              user?.firstName, 
              user?.lastName
            ];
            
            const currentData = queryClient.getQueryData(dashboardQueryKey);
            
            if (currentData && currentData.metrics) {
              const updatedData = { ...currentData };
              const metrics = { ...updatedData.metrics };
              
              metrics.new_opportunities = (metrics.new_opportunities || 0) + 1;
              
              updatedData.metrics = metrics;
              queryClient.setQueryData(dashboardQueryKey, updatedData);
              console.log('📊 [useRealTimeNotifications] ✅ New opportunity cache updated directly');
            } else {
              console.log('📊 [useRealTimeNotifications] ⚠️ No cache data for new opportunity, invalidating query');
              queryClient.invalidateQueries({ queryKey: dashboardQueryKey });
            }
            
            // Show metric card animation - use correct widget ID
            if (window.showFloatingNotification) {
              window.showFloatingNotification('add', 1, { 
                targetWidget: 'new_opportunities-widget'
              });
            }
            
            // Also invalidate notifications for the notification list
            queryClient.invalidateQueries({ queryKey: ['notifications'] });
          } else if (analyticsData.update_type === 'closing_soon') {
            console.log('📊 [useRealTimeNotifications] Opportunity closing soon - showing metric card animation');
            
            // Update the cache directly for immediate UI update
            const dashboardQueryKey = [
              'metrics', 
              'dashboard', 
              user?.user_id, 
              user?.userType || user?.user_type, 
              user?.firstName, 
              user?.lastName
            ];
            
            const currentData = queryClient.getQueryData(dashboardQueryKey);
            
            if (currentData && currentData.metrics) {
              const updatedData = { ...currentData };
              const metrics = { ...updatedData.metrics };
              
              metrics.closing_soon_count = (metrics.closing_soon_count || 0) + 1;
              
              updatedData.metrics = metrics;
              queryClient.setQueryData(dashboardQueryKey, updatedData);
              console.log('📊 [useRealTimeNotifications] ✅ Closing soon cache updated directly');
            } else {
              console.log('📊 [useRealTimeNotifications] ⚠️ No cache data for closing soon, invalidating query');
              queryClient.invalidateQueries({ queryKey: dashboardQueryKey });
            }
            
            // Show metric card animation - use correct widget ID
            if (window.showFloatingNotification) {
              window.showFloatingNotification('add', 1, { 
                targetWidget: 'closing_soon-widget'
              });
            }
            
            // Also invalidate notifications for the notification list
            queryClient.invalidateQueries({ queryKey: ['notifications'] });
          }
          break;
        }
          
        case 'performance_metrics_update': {
          console.log('📊 [useRealTimeNotifications] Performance metrics update:', analyticsData);
          
          // Invalidate performance-related queries
          queryClient.invalidateQueries({ queryKey: ['performance'] });
          queryClient.invalidateQueries({ queryKey: ['entities'] });
          
          // Show toast notification for overdue items
          if (analyticsData.update_type === 'overdue_notification') {
            const isBatch = analyticsData.is_batch;
            const entityCount = analyticsData.entity_count || 1;
            
            if (isBatch) {
              notifyInfo(`⏰ ${entityCount} entities have overdue performance data`);
            } else {
              notifyInfo(`⏰ Performance data overdue - check notifications`);
            }
          }
          break;
        }

        default:
          console.log('📊 [useRealTimeNotifications] Unknown analytics update type:', analyticsData.type);
          break;
      }
    };

    // Add event listeners for WebSocket events
    console.log('🔌 [useRealTimeNotifications] Adding event listeners');
    document.addEventListener('websocket:new_message', handleNewMessage);
    document.addEventListener('websocket:user_status', handleUserStatus);
    document.addEventListener('websocket:typing', handleTypingIndicator);
    document.addEventListener('websocket:notification', handleNotification);
    document.addEventListener('websocket:analytics_update', handleAnalyticsUpdate);
    console.log('🔌 [useRealTimeNotifications] Event listeners added successfully');

    return () => {
      // Clean up event listeners
      console.log('🔌 [useRealTimeNotifications] Cleaning up event listeners');
      document.removeEventListener('websocket:new_message', handleNewMessage);
      document.removeEventListener('websocket:user_status', handleUserStatus);
      document.removeEventListener('websocket:typing', handleTypingIndicator);
      document.removeEventListener('websocket:notification', handleNotification);
      document.removeEventListener('websocket:analytics_update', handleAnalyticsUpdate);
    };
  }, [user, queryClient, notifyInfo]);

  // Push permission is requested via PushPermissionPrompt (mounted in DashboardContainer)
  // which handles UX, 14-day snooze, and subscription registration.
};

export default useRealTimeNotifications; 